import { create } from 'zustand';
import type { ApiKey } from '@/domain/entities';
import { apiKeysApi } from '@/infrastructure/services/api-client';

interface ApiKeysState {
    keys: ApiKey[];
    isLoading: boolean;
}

interface ApiKeysActions {
    fetchKeys: () => Promise<void>;
    generateKey: (name: string, planId: string) => Promise<string>; // returns fullKey
    revokeKey: (keyId: string) => Promise<void>;
    getActiveKeys: () => ApiKey[];
}

type ApiKeysStore = ApiKeysState & ApiKeysActions;

export const useApiKeysStore = create<ApiKeysStore>()((set, get) => ({
    keys: [],
    isLoading: false,

    fetchKeys: async () => {
        set({ isLoading: true });
        try {
            const result = await apiKeysApi.list();
            set({
                keys: result.keys.map((k) => ({
                    id: k.id,
                    name: k.name,
                    maskedKey: k.maskedKey,
                    status: k.status,
                    planId: k.planId,
                    createdAt: new Date(k.createdAt),
                    revokedAt: k.revokedAt ? new Date(k.revokedAt) : undefined,
                })),
                isLoading: false,
            });
        } catch {
            set({ isLoading: false });
        }
    },

    generateKey: async (name: string, planId: string): Promise<string> => {
        const result = await apiKeysApi.generate({ name, planId });
        const newKey: ApiKey = {
            id: result.apiKey.id,
            name: result.apiKey.name,
            maskedKey: result.apiKey.maskedKey,
            status: result.apiKey.status,
            planId: result.apiKey.planId,
            createdAt: new Date(result.apiKey.createdAt),
            revokedAt: result.apiKey.revokedAt ? new Date(result.apiKey.revokedAt) : undefined,
        };
        set((state) => ({ keys: [newKey, ...state.keys] }));
        return result.fullKey;
    },

    revokeKey: async (keyId: string) => {
        await apiKeysApi.revoke(keyId);
        set((state) => ({
            keys: state.keys.map((k) =>
                k.id === keyId ? { ...k, status: 'revoked' as const, revokedAt: new Date() } : k,
            ),
        }));
    },

    getActiveKeys: (): ApiKey[] => {
        return get().keys.filter((k) => k.status === 'active');
    },
}));

export default useApiKeysStore;
